/*
  React Query cache keys for the credential manager.

  Kept in their own module so a mutation can invalidate a list it does not
  otherwise import, and so the key shapes stay consistent — every key starts with
  its feature name, which makes a broad `invalidateQueries({ queryKey: X.all })`
  do the obvious thing.
*/

import type { RequestLogFilters } from './requests'
import type { GetUsersRequest } from './user-management.types'
import type {
  DeepDiveService,
  EnvelopeGroupBy,
  EnvelopesQuery,
  MessagingProvider,
  SalesPeriodSelection,
  MessagingWindow,
  SalesFilterSelection,
  SalesPlatform,
} from './deep-dive.types'

export const credentialKeys = {
  all: ['credentials'] as const,
  search: (q: string) => [...credentialKeys.all, 'search', q] as const,
}

export const credentialAuditKeys = {
  all: ['credential-audit-logs'] as const,
  list: (page: number, pageSize: number, action?: string) =>
    [...credentialAuditKeys.all, page, pageSize, action ?? 'all'] as const,
}

export const requestKeys = {
  all: ['requests'] as const,
  pending: (page: number, pageSize: number) =>
    [...requestKeys.all, 'pending', page, pageSize] as const,
  rotationPending: (page: number, pageSize: number) =>
    [...requestKeys.all, 'rotation-pending', page, pageSize] as const,
  logs: (filters: RequestLogFilters) => [...requestKeys.all, 'logs', filters] as const,
}

export const userKeys = {
  all: ['users'] as const,
  /** The registry slice for one set of filters — the server does the filtering. */
  list: (params: GetUsersRequest) => [...userKeys.all, 'list', params] as const,
  /**
   * Roles, departments and platforms. Separate from `all` so a create/update can
   * invalidate both the list and the role counts without refetching one twice.
   */
  metadata: () => [...userKeys.all, 'metadata'] as const,
  /** GA4 active-user counts for one inclusive date range. */
  activeStats: (start: string, end: string) =>
    [...userKeys.all, 'active-stats', start, end] as const,
  /** The signed-in user's own registry row, looked up by their email. */
  me: (email: string) => [...userKeys.all, 'me', email] as const,
}

/**
 * The Phase 2 deep-dive screens.
 *
 * Every sales key carries the filter selection, so changing a chip is a new
 * cache entry rather than a refetch of the same one — going back to "All" then
 * re-picks the answer already in cache instead of hitting an expensive upstream
 * again.
 */
export const deepDiveKeys = {
  all: ['deep-dive'] as const,
  overview: (service: DeepDiveService) => [...deepDiveKeys.all, service, 'overview'] as const,
  incidents: (service: DeepDiveService, limit: number) =>
    [...deepDiveKeys.all, service, 'incidents', limit] as const,
  platformIncidents: (key: string, period: string) =>
    [...deepDiveKeys.all, 'platforms', key, 'incidents', period] as const,
  salesOverview: (service: DeepDiveService, date: string | undefined, f: SalesFilterSelection) =>
    [...deepDiveKeys.all, service, 'sales', 'overview', date ?? 'today', f] as const,
  salesTimeline: (service: DeepDiveService, date: string | undefined, f: SalesFilterSelection) =>
    [...deepDiveKeys.all, service, 'sales', 'timeline', date ?? 'today', f] as const,
  salesQuarterly: (service: DeepDiveService, year: number | undefined, f: SalesFilterSelection) =>
    [...deepDiveKeys.all, service, 'sales', 'quarterly', year ?? 'current', f] as const,
  /**
   * Period totals and series. The whole selection goes in the key — a different
   * period, custom range or compare mode is a different question, and each
   * answer is worth keeping so stepping back to one is instant.
   */
  salesSummary: (service: DeepDiveService, p: SalesPeriodSelection, f: SalesFilterSelection) =>
    [...deepDiveKeys.all, service, 'sales', 'summary', p, f] as const,
  salesSeries: (service: DeepDiveService, p: SalesPeriodSelection, f: SalesFilterSelection) =>
    [...deepDiveKeys.all, service, 'sales', 'series', p, f] as const,
  /** Keyed by platform, not by service: one catalogue serves Aurora and Solo alike. */
  salesFilters: (platform: SalesPlatform) =>
    [...deepDiveKeys.all, 'sales-filters', platform] as const,
  /*
    Keyed by route family. The two families are independent — separate vendors,
    separate syncs, separate failures — so nothing may share a cache entry
    between them, and a screen that fetched one must never be served the other's
    response.
  */
  messaging: (window: MessagingWindow, provider: MessagingProvider) =>
    [...deepDiveKeys.all, 'messaging', provider, window] as const,
  messagingHourly: (window: MessagingWindow, provider: MessagingProvider) =>
    [...deepDiveKeys.all, 'messaging', provider, 'hourly', window] as const,
  messagingRejections: (limit: number, provider: MessagingProvider) =>
    [...deepDiveKeys.all, 'messaging', provider, 'rejections', limit] as const,
  messagingAlerts: (provider: MessagingProvider) =>
    [...deepDiveKeys.all, 'messaging', provider, 'alerts'] as const,
  envelopesOverview: (period: SalesPeriodSelection) =>
    [...deepDiveKeys.all, 'envelopes', 'overview', period] as const,
  envelopes: (query: EnvelopesQuery) => [...deepDiveKeys.all, 'envelopes', 'list', query] as const,
  envelopeGroups: (by: EnvelopeGroupBy, period: SalesPeriodSelection) =>
    [...deepDiveKeys.all, 'envelopes', 'groups', by, period] as const,
}

export const statsKeys = {
  all: ['stats'] as const,
  pending: () => [...statsKeys.all, 'pending'] as const,
  activity: () => [...statsKeys.all, 'activity'] as const,
  request: () => [...statsKeys.all, 'request'] as const,
}
